/* eslint-disable max-statements */

import { Box, Button, Group } from '@mantine/core';
import { UseFormReturnType } from '@mantine/form';
import { modals } from '@mantine/modals';
import { useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { TransitGroup, TransitHouseConsignment } from '@e-origin/shared';

import { SearchBox, Table } from '@components';
import { TransitViewHouseFormValues } from '../validateTransitViewHouseForm';
import { EditHouse } from './EditHouse';
import { useHouseDetailsColumns } from './useHouseDetailsColumns';

interface HouseDetailsProps {
  form: UseFormReturnType<TransitViewHouseFormValues>;
  editDisabled: boolean;
  editDisabledConditionalForArrival: boolean;
}

export const HouseDetails: React.FC<HouseDetailsProps> = ({ form, editDisabled, editDisabledConditionalForArrival }) => {
  const routeLocation = useLocation();
  const queryParams = useRef<URLSearchParams>(new URLSearchParams(routeLocation.search));
  const navigate = useNavigate();
  const isArrival = form?.values?.generalInfo?.group === TransitGroup.ARRIVAL;

  const [searchQuery, setSearchQuery] = useState(queryParams.current.get('houseSearch') ? queryParams.current.get('houseSearch') : '');
  const selectedRow = useRef<{ houseConsignmentIdx: number; houseConsigment: TransitHouseConsignment } | null>(null);

  const houseConsigments = useMemo(
    () =>
      (form?.values?.Consignment?.HouseConsignment || []).filter((house) => {
        if (!searchQuery) {
          return true;
        }

        return house.ConsignmentItem.find(
          (item) =>
            item.Commodity?.descriptionOfGoods?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            item.Commodity?.CommodityCode?.hsCode?.toLowerCase().includes(searchQuery.toLowerCase()),
        );
      }),
    [form?.values?.Consignment?.HouseConsignment, searchQuery],
  );

  const handleSave = (houseConsignment) => {
    const houseConsignmentIdx = selectedRow.current?.houseConsignmentIdx ?? -1;
    if (houseConsignmentIdx >= 0) {
      const oldHouse = form?.values?.Consignment.HouseConsignment[houseConsignmentIdx];
      form.setFieldValue(`Consignment.HouseConsignment.${houseConsignmentIdx}`, {
        ...houseConsignment,
        ConsignmentItem: oldHouse.ConsignmentItem,
      });
    } else {
      form.setFieldValue('Consignment.HouseConsignment', [...(form?.values?.Consignment.HouseConsignment || []), houseConsignment]);
    }

    selectedRow.current = null;
  };

  const handleSearch = (value: string) => {
    setSearchQuery(value);

    queryParams.current.set('houseSearch', value);
    navigate({ search: queryParams.current.toString() });
  };

  const openHouseConsignment = (houseConsignmentIdx?) => {
    modals.open({
      title: 'House consignment',
      size: 'xl',
      children: (
        <EditHouse
          form={form}
          houseConsignmentIdx={houseConsignmentIdx}
          onSave={handleSave}
          onHide={modals.closeAll}
          editDisabled={editDisabled}
          editDisabledConditionalForArrival={editDisabledConditionalForArrival}
          isArrival={isArrival}
        />
      ),
    });
  };

  const { columns } = useHouseDetailsColumns({
    form,
    isArrival,
    openHouseConsignment,
    queryParams,
    editDisabled,
    editDisabledConditionalForArrival,
    selectedRow,
    houseConsigments,
  });

  return (
    <>
      <Group display={'flex'} py={12} justify="end" gap={10}>
        <SearchBox handleSearch={handleSearch} value={searchQuery} placeholder="Search goods by HS code, description of goods" />
        {!editDisabledConditionalForArrival && (
          <Button onClick={openHouseConsignment} variant="outline" size="lg" data-pw="add-house-btn">
            Add new
          </Button>
        )}
      </Group>
      <Box mb={20}>
        <Table key={searchQuery} columns={columns} dataSource={houseConsigments || []} pagination verticalSpacing="xs" />
      </Box>
    </>
  );
};
